/**
 * Use case configurations with workflow steps
 */

import React from 'react';
import type { TFunction } from 'i18next';
import { Stethoscope, Users, Heart, Scale, BookOpen, FileText } from 'lucide-react';
import { UseCase, UseCaseType } from '../models/UseCase';
import { WorkflowStep, GenogramContext } from '../models/Workflow';
import i18n from '../../../i18n/config';

// Helper functions for workflow conditions
function hasParents(node: any, context: GenogramContext): boolean {
  return context.edges.some(
    edge => edge.type === 'child' && edge.to === node.id
  );
}

function hasSiblings(node: any, context: GenogramContext): boolean {
  const parentEdges = context.edges.filter(
    edge => edge.type === 'child' && edge.to === node.id
  );

  if (parentEdges.length === 0) return false;

  const parents = parentEdges.map(e => e.from);
  const siblingEdges = context.edges.filter(
    edge => edge.type === 'child' &&
            parents.includes(edge.from) &&
            edge.to !== node.id
  );

  return siblingEdges.length > 0;
}

function hasGrandparents(node: any, context: GenogramContext): boolean {
  const parentIds = context.edges
    .filter(edge => edge.type === 'child' && edge.to === node.id)
    .map(edge => edge.from);

  for (const parentId of parentIds) {
    const hasParent = context.edges.some(
      edge => edge.type === 'child' && edge.to === parentId
    );
    if (hasParent) return true;
  }

  return false;
}

// Medical/Genetic workflow steps
const medicalWorkflowSteps: WorkflowStep[] = [
  {
    id: 'add-index-person',
    title: 'Add Index Person',
    description: 'Start by adding the primary person (patient/proband)',
    condition: (ctx) => ctx.nodes.length === 0,
    actions: [
      { type: 'add-person', label: 'Add Index Person', priority: 'high' }
    ],
    completionCriteria: (ctx) => ctx.nodes.length >= 1,
    skippable: false
  },
  {
    id: 'add-parents',
    title: 'Add Parents',
    description: 'Add biological parents for the index person',
    condition: (ctx) => {
      const indexPerson = ctx.nodes.find(n => n.type === 'person');
      return indexPerson ? !hasParents(indexPerson, ctx) : false;
    },
    actions: [
      {
        type: 'add-parent',
        label: 'Add Parents',
        priority: 'high'
      }
    ],
    completionCriteria: (ctx) => {
      const indexPerson = ctx.nodes[0];
      return indexPerson ? hasParents(indexPerson, ctx) : false;
    },
    skippable: true
  },
  {
    id: 'add-siblings',
    title: 'Add Siblings',
    description: 'Add brothers and sisters (if any)',
    condition: (ctx) => {
      const indexPerson = ctx.nodes[0];
      return indexPerson ? hasParents(indexPerson, ctx) : false;
    },
    actions: [
      { type: 'add-sibling', label: 'Add Sibling', priority: 'medium' },
      { type: 'skip-step', label: 'No siblings', priority: 'low' }
    ],
    completionCriteria: (ctx) => {
      return ctx.skippedSteps.includes('add-siblings') ||
             (ctx.nodes[0] ? hasSiblings(ctx.nodes[0], ctx) : false);
    },
    skippable: true
  },
  {
    id: 'add-grandparents',
    title: 'Add Grandparents',
    description: 'Add parents for mother and father (2 generations)',
    condition: (ctx) => ctx.nodes[0] ? hasParents(ctx.nodes[0], ctx) : false,
    actions: [
      { type: 'add-grandparents', label: 'Add Grandparents', priority: 'medium' },
      { type: 'skip-step', label: 'Skip grandparents', priority: 'low' }
    ],
    completionCriteria: (ctx) => {
      return ctx.skippedSteps.includes('add-grandparents') ||
             (ctx.nodes[0] ? hasGrandparents(ctx.nodes[0], ctx) : false);
    },
    skippable: true
  },
  {
    id: 'complete-medical-info',
    title: 'Complete Medical Information',
    description: 'Add health conditions and medical history',
    condition: (ctx) => ctx.nodes.length >= 3,
    actions: [
      {
        type: 'batch-edit-health',
        label: 'Fill Medical Details',
        priority: 'high'
      }
    ],
    completionCriteria: (ctx) => {
      const nodesWithHealth = ctx.nodes.filter(n =>
        n.medicalConditionTags && n.medicalConditionTags.length > 0
      );
      return nodesWithHealth.length >= ctx.nodes.length * 0.5;
    },
    skippable: false
  }
];

// Family Therapy workflow steps (simplified for now)
const therapyWorkflowSteps: WorkflowStep[] = [
  {
    id: 'add-index-person',
    title: 'Add Primary Client',
    description: 'Start by adding the main person in therapy',
    condition: (ctx) => ctx.nodes.length === 0,
    actions: [
      { type: 'add-person', label: 'Add Client', priority: 'high' }
    ],
    completionCriteria: (ctx) => ctx.nodes.length >= 1,
    skippable: false
  },
  {
    id: 'add-family-members',
    title: 'Add Immediate Family',
    description: 'Add spouse, children, parents, or siblings',
    condition: (ctx) => ctx.nodes.length >= 1,
    actions: [
      { type: 'add-family', label: 'Add Family Members', priority: 'high' }
    ],
    completionCriteria: (ctx) => ctx.nodes.length >= 3,
    skippable: false
  }
];

// Social Work workflow steps
const socialWorkWorkflowSteps: WorkflowStep[] = [
  {
    id: 'add-index-person',
    title: 'Add Client',
    description: 'Start by adding the person receiving services',
    condition: (ctx) => ctx.nodes.length === 0,
    actions: [
      { type: 'add-person', label: 'Add Client', priority: 'high' }
    ],
    completionCriteria: (ctx) => ctx.nodes.length >= 1,
    skippable: false
  }
];

// Legal/Estate workflow steps
const legalWorkflowSteps: WorkflowStep[] = [
  {
    id: 'add-index-person',
    title: 'Add Primary Individual',
    description: 'Start by adding the person for estate/legal planning',
    condition: (ctx) => ctx.nodes.length === 0,
    actions: [
      { type: 'add-person', label: 'Add Individual', priority: 'high' }
    ],
    completionCriteria: (ctx) => ctx.nodes.length >= 1,
    skippable: false
  }
];

// General Research workflow steps
const generalWorkflowSteps: WorkflowStep[] = [
  {
    id: 'add-first-person',
    title: 'Add First Person',
    description: 'Start building your family tree',
    condition: (ctx) => ctx.nodes.length === 0,
    actions: [
      { type: 'add-person', label: 'Add Person', priority: 'high' }
    ],
    completionCriteria: (ctx) => ctx.nodes.length >= 1,
    skippable: false
  }
];

// Use case configurations
// Note: Family Therapy is first - highest conversion correlation per analytics
const buildUseCases = (t: TFunction<'genogram'>): UseCase[] => [
  
  {
    id: 'blank',
    name: t('useCases.blank.name', { defaultValue: 'Start with a Blank Canvas' }) as string,
    description: t('useCases.blank.description', { defaultValue: 'Begin with an empty genogram' }) as string,
    detailedDescription: t('useCases.blank.detailedDescription', { defaultValue: 'Choose this if you want to explore freely without guided workflows or just need a basic family diagram.' }) as string,
    icon: React.createElement(FileText, { className: 'size-6 lg:size-12' }),
    color: '#6b7280',
    iconGradient: 'from-[#cbd5e1] via-[#e2e8f0] to-[#cbd5e1]',
    professionals: [
      t('useCases.blank.professionals.allUsers', { defaultValue: 'All Users' }) as string,
    ],
    guidedSteps: [],
    examples: [],
    dataFields: []
  },
 
  {
    id: 'family-therapy',
    name: t('useCases.family-therapy.name', { defaultValue: 'Family Therapy & Counseling' }) as string,
    description: t('useCases.family-therapy.description', { defaultValue: 'Explore family dynamics and relationship patterns' }) as string,
    detailedDescription: t('useCases.family-therapy.detailedDescription', { defaultValue: 'Designed for marriage and family therapists, psychologists, and counselors working with family systems and relationship issues.' }) as string,
    icon: React.createElement(Users, { className: 'size-6 lg:size-12' }),
    color: '#8b5cf6',
    iconGradient: 'from-[#ff9f7f] via-[#ff5f8a] to-[#ff2f6f]',
    professionals: [
      t('useCases.family-therapy.professionals.therapists', { defaultValue: 'Therapists' }) as string,
      t('useCases.family-therapy.professionals.counselors', { defaultValue: 'Counselors' }) as string,
      t('useCases.family-therapy.professionals.psychologists', { defaultValue: 'Psychologists' }) as string,
    ],
    guidedSteps: therapyWorkflowSteps,
    examples: ['blended-family', 'trauma-pattern'],
    dataFields: ['relationships', 'traumaEvents', 'mentalHealth']
  },

  {
    id: 'medical-genetic',
    name: t('useCases.medical-genetic.name', { defaultValue: 'Medical & Genetic History' }) as string,
    description: t('useCases.medical-genetic.description', { defaultValue: 'Track hereditary conditions and health patterns' }) as string,
    detailedDescription: t('useCases.medical-genetic.detailedDescription', { defaultValue: 'Perfect for healthcare providers, genetic counselors, and medical researchers documenting family health history and hereditary conditions.' }) as string,
    icon: React.createElement(Stethoscope, { className: 'size-6 lg:size-12' }),
    color: '#3b82f6',
    iconGradient: 'from-[#3fb4ff] via-[#1f8dff] to-[#006eff]',
    professionals: [
      t('useCases.medical-genetic.professionals.doctors', { defaultValue: 'Doctors' }) as string,
      t('useCases.medical-genetic.professionals.geneticCounselors', { defaultValue: 'Genetic Counselors' }) as string,
      t('useCases.medical-genetic.professionals.nurses', { defaultValue: 'Nurses' }) as string,
    ],
    guidedSteps: medicalWorkflowSteps,
    examples: ['brca-family', 'diabetes-pedigree'],
    dataFields: ['birthDate', 'medicalConditions', 'causeOfDeath']
  },

  {
    id: 'social-work',
    name: t('useCases.social-work.name', { defaultValue: 'Social Work & Child Welfare' }) as string,
    description: t('useCases.social-work.description', { defaultValue: 'Document family structure and support systems' }) as string,
    detailedDescription: t('useCases.social-work.detailedDescription', { defaultValue: 'Built for social workers, child welfare professionals, and case managers assessing family resources and placement options.' }) as string,
    icon: React.createElement(Heart, { className: 'size-6 lg:size-12' }),
    color: '#ef4444',
    iconGradient: 'from-[#2ad89e] via-[#17c07c] to-[#04ac64]',
    professionals: [
      t('useCases.social-work.professionals.socialWorkers', { defaultValue: 'Social Workers' }) as string,
      t('useCases.social-work.professionals.caseManagers', { defaultValue: 'Case Managers' }) as string,
    ],
    guidedSteps: socialWorkWorkflowSteps,
    examples: ['foster-placement', 'support-network'],
    dataFields: ['livingArrangement', 'supportSystems', 'legalInvolvement']
  },
  {
    id: 'legal-estate',
    name: t('useCases.legal-estate.name', { defaultValue: 'Legal & Estate Planning' }) as string,
    description: t('useCases.legal-estate.description', { defaultValue: 'Map family relationships for legal documentation' }) as string,
    detailedDescription: t('useCases.legal-estate.detailedDescription', { defaultValue: 'Tailored for estate attorneys, family law practitioners, and financial planners documenting inheritance and beneficiary relationships.' }) as string,
    icon: React.createElement(Scale, { className: 'size-6 lg:size-12' }),
    color: '#f59e0b',
    iconGradient: 'from-[#c591ff] via-[#9b73ff] to-[#7b5bff]',
    professionals: [
      t('useCases.legal-estate.professionals.attorneys', { defaultValue: 'Attorneys' }) as string,
      t('useCases.legal-estate.professionals.estatePlanners', { defaultValue: 'Estate Planners' }) as string,
    ],
    guidedSteps: legalWorkflowSteps,
    examples: ['estate-complex', 'inheritance-tree'],
    dataFields: ['maritalHistory', 'legalName', 'estateInfo']
  },
  {
    id: 'general-research',
    name: t('useCases.general-research.name', { defaultValue: 'General Research & Genealogy' }) as string,
    description: t('useCases.general-research.description', { defaultValue: 'Create a family tree or genealogical record' }) as string,
    detailedDescription: t('useCases.general-research.detailedDescription', { defaultValue: 'For genealogists, family historians, and personal use documenting family history and ancestry.' }) as string,
    icon: React.createElement(BookOpen, { className: 'size-6 lg:size-12' }),
    color: '#10b981',
    iconGradient: 'from-[#ffe58f] via-[#f6c948] to-[#f59e0b]',
    professionals: [
      t('useCases.general-research.professionals.genealogists', { defaultValue: 'Genealogists' }) as string,
      t('useCases.general-research.professionals.researchers', { defaultValue: 'Researchers' }) as string,
      t('useCases.general-research.professionals.families', { defaultValue: 'Families' }) as string,
    ],
    guidedSteps: generalWorkflowSteps,
    examples: ['family-tree', 'ancestry-chart'],
    dataFields: ['birthPlace', 'culturalHeritage', 'immigration']
  }
  
  
];

export function getUseCases(lang?: string): UseCase[] {
  const t = i18n.getFixedT(lang ?? i18n.language, 'genogram');
  return buildUseCases(t);
}

// Helper to get use case by ID
export function getUseCaseById(id: UseCaseType): UseCase | undefined {
  return getUseCases().find(uc => uc.id === id);
}
